import { create } from "zustand";

const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";

const defaultFormFields = [
  { label: "Email", required: true },
  { label: "Phone", required: true },
  { label: "Name", required: true },
  { label: "Zip Code", required: true },
];

const getDefaultEventDetails = () => ({
  campaignId: 0,
  campaignName: "",
  startDate: "",
  durationInMinutes: 30,
  eventTime: "",
  description: "",
  formFields: defaultFormFields,
  eventPriceTagLine: "",
  eventPrice: "",
  templateId: 0,
  eventTemplateId: 0,
  templateCategory: "",
  templateImage: "",
  templateHtml: "",
  templateSummary: "",
  videoConferenceLink: "",
  isDraft: true,
});

const getDefaultCanvasData = () => ({
  textElements: [],
  shapes: [],
  hydrated: false,
});

const normalizeTemplateImage = (imagePath) => {
  if (!imagePath) return "";
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }
  return BASE_URL ? `${BASE_URL}${imagePath}` : imagePath;
};

const convertUTCToIST_HHMMSS = (utcTimeString) => {
  if (!utcTimeString || typeof utcTimeString !== "string") return "";
  const [hours, minutes, seconds = 0] = utcTimeString.split(":").map(Number);
  const utcDate = new Date(Date.UTC(1970, 0, 1, hours, minutes, seconds));
  const istDate = new Date(utcDate.getTime() + 5.5 * 60 * 60 * 1000);
  const istHours = String(istDate.getUTCHours()).padStart(2, "0");
  const istMinutes = String(istDate.getUTCMinutes()).padStart(2, "0");
  const istSeconds = String(istDate.getUTCSeconds()).padStart(2, "0");
  return `${istHours}:${istMinutes}:${istSeconds}`;
};

const mapApiEventToEventDetails = (ev) => ({
  campaignId: ev?.id || 0,
  campaignName: ev?.eventName || "",
  startDate: ev?.startDate ? ev.startDate.substring(0, 10) : "",
  durationInMinutes: Number(ev?.durationInMinutes) || 30,
  eventTime: ev?.eventTime ? convertUTCToIST_HHMMSS(ev.eventTime) : "",
  description: ev?.eventDetails || "",
  formFields:
    (ev?.eventInputs || []).map((inp) => ({
      label: inp.inputTitle,
      required: inp.isRequire,
    })) || defaultFormFields,
  eventPriceTagLine: ev?.eventPriceTagline || "",
  eventPrice: ev?.eventEnterPrice?.toString() || "0",
  templateId: ev?.eventTemplate?.templateId || 0,
  eventTemplateId: ev?.eventTemplate?.id || 0,
  templateCategory: ev?.eventTemplate?.templateSummary || "",
  templateImage: normalizeTemplateImage(ev?.eventTemplate?.templateImage),
  templateHtml: ev?.eventTemplate?.templateBody || "",
  templateSummary: ev?.eventTemplate?.templateSummary || "",
  videoConferenceLink: ev?.meetingLink || "",
  isDraft: ev?.isDraft ?? true,
});

const normalizeEventDetailsPatch = (patch) => {
  if (!patch || typeof patch !== "object") return patch;
  if (!Object.prototype.hasOwnProperty.call(patch, "templateImage")) return patch;

  return {
    ...patch,
    templateImage: normalizeTemplateImage(patch.templateImage || ""),
  };
};

export const useEventStore = create((set, get) => ({
  mode: "create",
  eventId: null,
  selectedTemplate: null,
  eventDetails: getDefaultEventDetails(),
  canvasData: getDefaultCanvasData(),
  isDirty: false,
  lastSavedAt: null,
  loadedEventId: null,

  setMode: (mode) => set({ mode }),
  setEventId: (eventId) => set({ eventId }),

  updateEventDetails: (patch, markDirty = true) =>
    set((state) => ({
      // Keep template image source stable regardless of API/snapshot relative paths.
      // This prevents canvas size fallback and unintended element rescaling on reload/save.
      eventDetails: {
        ...state.eventDetails,
        ...normalizeEventDetailsPatch(
          typeof patch === "function" ? patch(state.eventDetails) : patch,
        ),
      },
      isDirty: markDirty ? true : state.isDirty,
    })),

  updateCanvasData: (patch, markDirty = true) =>
    set((state) => ({
      canvasData: {
        ...state.canvasData,
        ...(typeof patch === "function" ? patch(state.canvasData) : patch),
      },
      isDirty: markDirty ? true : state.isDirty,
    })),

  setTextElements: (updater, markDirty = true) =>
    set((state) => ({
      canvasData: {
        ...state.canvasData,
        textElements:
          typeof updater === "function"
            ? updater(state.canvasData.textElements)
            : updater,
      },
      isDirty: markDirty ? true : state.isDirty,
    })),

  setShapes: (updater, markDirty = true) =>
    set((state) => ({
      canvasData: {
        ...state.canvasData,
        shapes:
          typeof updater === "function" ? updater(state.canvasData.shapes) : updater,
      },
      isDirty: markDirty ? true : state.isDirty,
    })),

  replaceCanvasData: ({ textElements = [], shapes = [], hydrated = true }) =>
    set((state) => ({
      canvasData: {
        ...state.canvasData,
        textElements,
        shapes,
        hydrated,
      },
    })),

  setTemplate: (template) =>
    set((state) => ({
      selectedTemplate: template,
      eventDetails: {
        ...state.eventDetails,
        templateId: template?.id || 0,
        templateImage: normalizeTemplateImage(template?.templateImage || ""),
        templateHtml: template?.templateBody || "",
        templateSummary: template?.templateDetails || "",
        templateCategory: template?.templateCategory || "",
      },
      canvasData: getDefaultCanvasData(),
      isDirty: true,
    })),

  loadEventData: (apiEvent) => {
    const mappedEvent = mapApiEventToEventDetails(apiEvent);
    set({
      mode: "edit",
      eventId: apiEvent?.id || null,
      loadedEventId: apiEvent?.id || null,
      selectedTemplate: apiEvent?.eventTemplate
        ? {
            id: apiEvent.eventTemplate.templateId,
            name: apiEvent.eventTemplate.templateName || "Template",
            templateBody: apiEvent.eventTemplate.templateBody,
            templateDetails: apiEvent.eventTemplate.templateSummary,
            templateCategory: apiEvent.eventTemplate.templateCategory || "",
            templateImage: apiEvent.eventTemplate.templateImage,
          }
        : null,
      eventDetails: mappedEvent,
      canvasData: getDefaultCanvasData(),
      isDirty: false,
      lastSavedAt: null,
    });
  },

  hydrateFromSnapshot: (snapshot, mode, eventId) =>
    set((state) => ({
      mode,
      eventId,
      selectedTemplate: snapshot?.selectedTemplate ?? state.selectedTemplate,
      eventDetails: snapshot?.eventDetails
        ? normalizeEventDetailsPatch(snapshot.eventDetails)
        : state.eventDetails,
      canvasData: snapshot?.canvasData ?? state.canvasData,
      isDirty: snapshot?.isDirty ?? state.isDirty,
      lastSavedAt: snapshot?.lastSavedAt ?? state.lastSavedAt,
      loadedEventId: eventId || state.loadedEventId,
    })),

  markSaved: () => set({ isDirty: false, lastSavedAt: new Date().toISOString() }),

  resetEvent: () =>
    set({
      mode: "create",
      eventId: null,
      selectedTemplate: null,
      eventDetails: getDefaultEventDetails(),
      canvasData: getDefaultCanvasData(),
      isDirty: false,
      lastSavedAt: null,
      loadedEventId: null,
    }),
}));
